import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';

import { environment } from '../../../environments/environment';
import { take } from 'rxjs/operators';
import { User } from '../interfaces/user';
import { TokenService } from './token.service';

const CLIENTE_ID_KEY = 'cliente_id';
const PRESTADOR_ID_KEY = 'prestador_id';

const API = environment.apiUrl + '/v1/users';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  usuarioSubject = new BehaviorSubject<User>(null);

  constructor(
    private tokenService: TokenService,
    private router: Router,
    private http: HttpClient,
    private permissionsService: NgxPermissionsService,
    private roleService: NgxRolesService
  ) {
    if (this.tokenService.hasToken())
      this.getDadosUsuario();

    if (this.usuario === null) {
      this.usuario = {
        id: 0,
        cliente_id: null,
        prestador_id: null
      };
    }
  }

  get usuario(): User {
    return JSON.parse(localStorage.getItem('perfil')) || null;
  }

  set usuario(usuario: User) {
    localStorage.setItem('perfil', JSON.stringify(usuario));
  }

  logout() {
    this.http.post(API + "/logout", null).subscribe();
    this.tokenService.removeToken();
    this.permissionsService.flushPermissions();
    this.roleService.flushRoles();
    this.usuario = new User();
    this.usuarioSubject.next(null);
    localStorage.removeItem(CLIENTE_ID_KEY);
    localStorage.removeItem(PRESTADOR_ID_KEY);
    this.router.navigateByUrl('/auth/login');
  }

  hasUserId() {
    if (this.usuario.id === 0) {
      this.tokenService.removeToken();
      this.router.navigateByUrl('/auth/login');
    }
  }

  isLogged() {
    return this.tokenService.hasToken();
  }

  getUser() {
    return this.usuarioSubject.asObservable();
  }

  getDadosUsuario() {
    return new Promise<User>(resolve => {
      this.http.get<User>(API + '/logado')
        .pipe(take(1))
        .subscribe(async usuario => {
          this.usuario = usuario;

          this.loadPermissions();
          this.loadRole();

          if (this.usuario.cliente_id)
            localStorage.setItem(CLIENTE_ID_KEY, this.usuario.cliente_id.toString());

          if (this.usuario.prestador_id)
            localStorage.setItem(PRESTADOR_ID_KEY, this.usuario.prestador_id.toString());

          this.usuarioSubject.next(this.usuario);

          resolve(this.usuario);
        });
    })
  }

  loadPermissions() {
    let permissionGroups: any = {};

    this.usuario.roles[0].permissions.forEach(value => {
      if (permissionGroups[value.model] === undefined)
        permissionGroups[value.model] = [];

      permissionGroups[value.model].push(value);
    });

    permissionGroups = Object.values(permissionGroups);

    permissionGroups.forEach(permissionGroup => {
      permissionGroup.forEach(permission => {
        this.permissionsService.addPermission(permission.slug);
      });
    });
  }

  loadRole() {
    this.roleService.addRole('admin', () => !!this.usuario.admin);
    this.roleService.addRole('cliente', () => this.usuario.prestador_id && this.usuario.cliente_id && !this.usuario.tecnico_id);
    this.roleService.addRole('tecnico', () => this.usuario.prestador_id && !this.usuario.cliente_id && this.usuario.tecnico_id);
    this.roleService.addRole('prestador', () => this.usuario.prestador_id && !this.usuario.cliente_id && !this.usuario.tecnico_id);
  }

  getUserId() {
    return this.usuario.id;
  }

  getClienteId() {
    if (!!localStorage.getItem(CLIENTE_ID_KEY)) {
      return parseInt(localStorage.getItem(CLIENTE_ID_KEY), 10);
    }

    return null;
  }

  getUserAcessos() {
    return this.permissionsService.getPermissions();
  }
}
